import { linkOptionsOf } from 'helper/taskLinks';

// The api's rule (contract § F): what it refuses with INVALID_ATTENDEES.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 200;

export const isValidGuestEmail = (email) =>
  email.length <= MAX_EMAIL_LENGTH && EMAIL_FORMAT.test(email);

const lower = (value) => (value || '').trim().toLowerCase();

// The api's people options carry no contactId: it sits at the end of their
// key, `p-<companyId>-<userId>`.
const contactIdOf = (option) =>
  option.contactId ??
  option.key?.match(/^p-[0-9a-f]{24}-([0-9a-f]{24})$/i)?.[1] ??
  null;

const memberName = (member) =>
  [member.firstname, member.lastname]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

/*
 * « Avec qui ? » of an appointment: links as « Associer à » of a task
 * (helper/taskLinks), plus a colleague of the CRM and any address typed. Each
 * keeps what the appointment needs of it: the option (admin/crm/contacts) of a
 * company or a contact (`optionKey`), the address that invites it (`email`).
 */
export const colleagueLink = (member) => ({
  type: 'colleague',
  id: member.id,
  label: memberName(member),
  sub: 'Collègue',
  email: lower(member.email),
});

export const recordLinkOf = (option) => ({
  ...linkOptionsOf([option])[0],
  optionKey: option.key,
  ...(option.contact ? { email: lower(option.email), contactId: contactIdOf(option) } : {}),
});

// What an appointment already made is held with, which its edit cannot change
// (the api reads neither its company nor its contact): shown, never removed
// nor invited.
export const lockedLinksOf = (item) => [
  ...(item.company?.id ? [{ type: 'company', id: item.company.id, label: item.company.name || '', locked: true }] : []),
  ...(item.contactName
    ? [
        {
          type: 'contact',
          id: item.contactId || `name:${item.contactName}`,
          label: item.contactName,
          sub: item.company?.name || '',
          companyId: item.company?.id ?? null,
          locked: true,
        },
      ]
    : []),
];

// A guest already on the appointment keeps what the api stored of it.
export const linkOfAttendee = (attendee) =>
  attendee.userId
    ? { ...colleagueLink({ id: attendee.userId }), label: attendee.displayName || attendee.email, email: lower(attendee.email), attendee }
    : {
        type: 'email',
        id: lower(attendee.email),
        label: attendee.displayName || attendee.email,
        sub: attendee.displayName ? attendee.email : '',
        email: lower(attendee.email),
        attendee,
      };

// A guest as the api takes it: a colleague by id, anyone else by address.
const toAttendeeInput = (attendee) =>
  attendee.userId
    ? { userId: attendee.userId }
    : {
        email: attendee.email,
        displayName: attendee.displayName || '',
        ...(attendee.contactId ? { contactId: attendee.contactId } : {}),
      };

/**
 * The guests the links invite: a colleague, a contact with an address, an
 * address typed (`colleaguesOnly`: an internal one, colleagues alone). A
 * company invites nobody, nor a contact without an address. Each once; the
 * owner of the calendar never.
 */
export const attendeesOf = (links, { ownerId = null, colleaguesOnly = false } = {}) => {
  const seen = new Set();
  return links
    .map((link) => {
      if (link.locked) return null;
      if (link.attendee) return toAttendeeInput(link.attendee);
      if (link.type === 'colleague') return { userId: link.id };
      if (colleaguesOnly || !link.email) return null;
      if (link.type === 'contact')
        return { email: link.email, displayName: link.label, ...(link.contactId ? { contactId: link.contactId } : {}) };
      if (link.type === 'email') return { email: link.email, displayName: '' };
      return null;
    })
    .filter((attendee) => {
      if (!attendee || (colleaguesOnly && !attendee.userId)) return false;
      if (attendee.userId && attendee.userId === ownerId) return false;
      const key = attendee.userId ? `u:${attendee.userId}` : `e:${attendee.email}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};
